import { ArrowRight, MapPin } from "lucide-react";
import { useEffect, useRef,useState } from "react";
import { Link } from "react-router-dom";
import Reveal from "./ui/reveal";
import DrawLine from "./ui/drawline";
import SplitText from "./ui/splittixt";
import Tilt3D from "./ui/tilt3D";
import Magnetic from "./ui/magnetic";
import PageHeader from "./pageHeader";

export const projectsData = {
  residential: [
    {
      id: "parkhills",
      name: "Gillco Parkhills",
      type: "Residential",
      location: "Airport Road, Mohali",
      status: "Ready to Move",
      price: "On Request",
      image: "/assets/parkhill/6.png",
      description: "2016 marked Gillco's premium residential addition to its portfolio with Gillco Parkhills, a monumental success at 19 stories high, right on the Mohali Airport Road.",
      features: ["19 Stories High", "3-4 BHK Options", "40+ Amenities", "Premium Clubhouse", "Swimming Pool", "Landscaped Gardens"],
      specs: { stories: "19", units: "200+", bhk: "3-4 BHK", area: "1800-2800 sq.ft." }
    },
    {
      id: "valley",
      name: "Gillco Valley",
      type: "Residential Township",
      location: "Kharar Highway, Mohali",
      status: "Delivered",
      price: "On Request",
      image: "/gillco-valley-main.jpg",
      description: "One of the largest townships in Mohali, Gillco Valley is home to thousands of happy families who enjoy world-class amenities and a vibrant community life.",
      features: ["Integrated Township", "Multiple Housing Options", "School Campus", "Shopping Complex", "Parks & Gardens", "24/7 Security"],
      specs: { stories: "Various", units: "5000+", bhk: "1-4 BHK", area: "600-2500 sq.ft." }
    },
    {
      id: "palms",
      name: "Gillco Palms",
      type: "Residential",
      location: "Sector 115, Mohali",
      status: "Under Construction",
      price: "Starting ₹45 Lac",
      image: "/assets/palm/4.jpg",
      description: "A new chapter in luxury living, Gillco Palms offers contemporary apartments designed for modern families seeking comfort and elegance.",
      features: ["Modern Architecture", "2-3 BHK Options", "Club Facilities", "Children's Play Area", "Jogging Track", "Multi-purpose Hall"],
      specs: { stories: "12", units: "150+", bhk: "2-3 BHK", area: "1200-1800 sq.ft." }
    }
  ],
  commercial: [
    {
      id: "business-hub",
      name: "Gillco Business Hub",
      type: "Commercial",
      location: "IT City Road, Mohali",
      status: "Ready to Move",
      price: "On Request",
      image: "/businessHub.jpg",
      description: "State-of-the-art commercial spaces designed for businesses of all sizes, featuring modern infrastructure and premium amenities.",
      features: ["Grade A Office Spaces", "High-speed Elevators", "24/7 Power Backup", "Ample Parking", "Food Court", "Conference Rooms"],
      specs: { floors: "10", spaces: "100+", sizes: "500-5000 sq.ft.", type: "Office Spaces" }
    },
    {
      id: "retail-plaza",
      name: "Gillco Retail Plaza",
      type: "Commercial",
      location: "Gillco Valley, Mohali",
      status: "Delivered",
      price: "On Request",
      image: "https://images.unsplash.com/photo-1567449303078-57ad995bd329?w=800",
      description: "A vibrant retail destination offering premium shop spaces in the heart of Gillco Valley township.",
      features: ["Prime Location", "High Footfall Area", "Branded Outlets", "Food Zone", "Parking Facility", "Security"],
      specs: { floors: "3", spaces: "50+", sizes: "200-2000 sq.ft.", type: "Retail Shops" }
    }
  ],
  industrial: [
    {
      id: "industrial-park",
      name: "Gillco Industrial Park",
      type: "Industrial",
      location: "IT City Road, Mohali",
      status: "Ready for Possession",
      price: "On Request",
      image: "/ind-park.jpg",
      description: "The Gillco Group channelized industrial growth with Gillco Industrial Park, offering world-class infrastructure for manufacturing and logistics.",
      features: ["Wide Internal Roads", "24/7 Security", "Power Infrastructure", "Water Supply", "Waste Management", "Green Zones"],
      specs: { area: "50+ Acres", plots: "200+", sizes: "500-10000 sq.yd.", type: "Industrial Plots" }
    }
  ]
};
const ProjectCard = ({ project }) => {
  return (
    <Link 
      to={`/projects/${project.id}`}
      className="group block bg-white border border-[rgba(0,0,0,0.05)] hover:border-[#0A4F3B] transition-all duration-500"
      data-testid={`project-card-${project.id}`}
    >
      <div className="relative overflow-hidden h-[300px]">
        <img 
          src={project.image}
          alt={project.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute top-4 left-4">
          <span className="bg-[#0A4F3B] text-white px-4 py-2 text-xs tracking-wider uppercase">
            {project.status}
          </span>
        </div>
      </div>
      <div className="p-6">
        <div className="flex items-center gap-2 text-[#4A4A4A] mb-2">
          <MapPin size={14} />
          <span className="font-body text-sm">{project.location}</span>
        </div>
        <h3 className="font-heading text-2xl text-[#0A0A0A] mb-2">{project.name}</h3>
        <p className="font-body text-sm text-[#0A4F3B] uppercase tracking-wider mb-4">{project.type}</p>
        <div className="flex items-center justify-between">
          <span className="font-body text-[#0A0A0A] font-medium">{project.price}</span>
          <span className="inline-flex items-center gap-2 text-[#0A4F3B] font-body text-sm group-hover:gap-3 transition-all">
            View Details <ArrowRight size={14} />
          </span>
        </div>
      </div>
    </Link>
  );
};
export const featuredProjects = [
  projectsData.residential[0], // Parkhills
  projectsData.commercial[0], // Business Hub
  projectsData.industrial[0]  // Industrial Park
];
const FeaturedProject = () => {
  const imgRef = useRef(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    let raf;
    const update = () => {
      if (!imgRef.current || !sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const vh = window.innerHeight;
      const progress = Math.max(0, Math.min(1, 1 - (rect.top / (vh * 0.5))));
      const scale = 0.88 + progress * 0.12;
      const radius = 20 - progress * 20;
      imgRef.current.style.transform = `scale(${scale})`;
      imgRef.current.style.borderRadius = `${radius}px`;
    };
    const onScroll = () => { raf = requestAnimationFrame(update); };
    window.addEventListener("scroll", onScroll, { passive: true });
    update();
    return () => { window.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, []);

  return (
    <section ref={sectionRef} className="py-24 md:py-32 bg-[#f8f6f2]" data-testid="v13-featured-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 mb-16">
        <Reveal>
          <div className="text-center">
            <div className="flex items-center justify-center gap-4 mb-6">
              <DrawLine className="w-12 h-[2px] bg-[#C4A265]" />
              <span className="text-xs uppercase tracking-[0.2em] text-[#C4A265] font-medium">Signature Development</span>
              <DrawLine className="w-12 h-[2px] bg-[#C4A265]" delay={0.1} />
            </div>
            <h2 className="text-3xl md:text-4xl text-[#1a1a1a]" style={{ fontFamily: "'Cormorant Garamond', serif" }}><SplitText text="Featured Project" /></h2>
          </div>
        </Reveal>
      </div>
      {/* Full-screen edge-to-edge image container — full image displayed (no cropping) */}
      <div className="relative">
        <div ref={imgRef} className="w-full overflow-hidden will-change-transform" style={{ transform: "scale(0.88)", borderRadius: "20px" }}>
          <img src="/assets/meraqui/1.jpg" alt="Gillco Meraqui" className="w-full h-auto block" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        </div>
        {/* Text panel: redesigned — clean editorial, ~10% on image, ~90% below */}
        <Reveal delay={0.2}>
          <div className="relative max-w-6xl mx-auto px-6 md:px-12 -mt-[60px] md:-mt-[80px] z-10">
            <div className="bg-white shadow-xl">
              {/* Top bar: eyebrow + location */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-[#C4A265]/25 px-8 md:px-12 lg:px-16 py-4">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-[1px] bg-[#C4A265]" />
                  <span className="text-[11px] uppercase tracking-[0.3em] text-[#C4A265] font-medium">Ultra Luxe Residential</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-[#4a4a4a]">
                  <MapPin size={15} className="text-[#C4A265]" />
                  <span style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-[15px]">Sector 126, Mohali, Punjab</span>
                </div>
              </div>

              {/* Body */}
              <div className="px-8 md:px-12 lg:px-16 py-8 md:py-10">
                <h3
                  className="text-3xl md:text-4xl lg:text-5xl text-[#0A0A0A] font-light leading-[1.05] mb-2"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  <SplitText text="Gillco Meraqui" delay={0.2} />
                  <span className="inline-block w-2 h-2 rounded-full bg-[#C4A265] ml-2 align-middle mb-2" />
                </h3>
                <div className="w-14 h-[2px] bg-[#C4A265] mb-5" />

                <Reveal delay={0.4}>
                  <p
                    className="text-[15px] md:text-base text-[#4a4a4a] leading-[1.75] max-w-3xl mb-7"
                    style={{ textAlign: "justify" }}
                  >
                    Indulge in the art of refined living at Meraqui, an upcoming residential masterpiece in Sector 126, Mohali. A haven of refined spaces, Meraqui features 444 exquisitely designed 3 &amp; 4 BHK luxury residences, spanning six residential towers, as well as a premium commercial space — offering a perfect blend of comfort, style and convenience.
                  </p>
                </Reveal>

                {/* Stats — clean horizontal row, no boxes */}
                <div className="border-t border-[#C4A265]/25 pt-6 mb-7" data-testid="v13-meraqui-stats">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
                    {[
                      { value: "12", label: "Acres" },
                      { value: "6", label: "Towers" },
                      { value: "3 / 4", label: "BHK Residences" },
                      { value: "444", label: "Residences" },
                    ].map((stat, i) => (
                      <Reveal key={i} delay={0.4 + i * 0.1} direction="up">
                        <div className="group">
                          <div
                            className="text-3xl md:text-4xl lg:text-5xl text-[#0A0A0A] font-light leading-none mb-2 whitespace-nowrap group-hover:text-[#0A4F3B] transition-colors duration-500"
                            style={{ fontFamily: "'Cormorant Garamond', serif" }}
                          >
                            {stat.value}
                          </div>
                          <div className="w-10 h-[1px] bg-[#C4A265] mb-2 group-hover:w-20 transition-all duration-500" />
                          <div className="text-[11px] uppercase tracking-[0.25em] text-[#4a4a4a]">{stat.label}</div>
                        </div>
                      </Reveal>
                    ))}
                  </div>
                </div>

                {/* CTA */}
                <Reveal delay={0.6}>
                  <Magnetic>
                    <Link
                      to={`https://gillcomeraqui.com`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-3 bg-[#0A4F3B] text-white tracking-[0.2em] uppercase text-xs px-8 py-4 hover:bg-[#0d6b4f] transition-all duration-300"
                      data-testid="v13-featured-cta"
                    >
                      Explore Meraqui <ArrowRight size={14} />
                    </Link>
                  </Magnetic>
                </Reveal>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
export default FeaturedProject;